import React, { useEffect, useState } from "react";
import { Badge, Button, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";

import { fetchAuditLogDetail } from "../../api/requests/audit-requests";
import { formatDateTime } from "../../pages/Tables/Paymenthistoryutils";
import { entityTypeLabel } from "./auditLabels";
import IdValue from "./IdValue";

/**
 * Tek denetim kaydının detayı.
 *
 * Liste satırı hafif tutulur (JSONB alanları hiç okunmaz); "ne değişti" sorusu ancak
 * burada cevaplanır. Detay ayrı bir istekle çekilir — listeyi bu alanlarla taşımak
 * her sayfada gereksiz veri okumak olurdu.
 */

/** Değişen alanların insan okunur adları; listede olmayan alan ham adıyla gösterilir. */
const FIELD_LABELS = {
    roles: "Roller",
    enabled: "Aktif",
    email: "E-posta",
    username: "Kullanıcı adı",
    phoneNumber: "Telefon",
    deleted: "Silinmiş",
    active: "Aktif",
    companyName: "Firma adı",
    contactEmail: "İletişim e-postası",
    merchantId: "Üye iş yeri no",
    merchantKey: "Üye iş yeri anahtarı",
    merchantSalt: "Üye iş yeri tuzu",
    storeKey: "Mağaza anahtarı",
    apiKey: "API anahtarı",
    testMode: "Test modu",
    amount: "Tutar",
    currency: "Para birimi",
    status: "Durum",
    provider: "Sağlayıcı",
    channel: "Kanal",
    // Denetim detaylarında sık geçen ama karşılıksız kalıp ham görünen alanlar
    oldStatus: "Önceki durum",
    newStatus: "Yeni durum",
    merchantOid: "Sağlayıcı sipariş no",
    installmentCount: "Taksit sayısı",
    refundClass: "İade türü",
    refundedAmount: "İade tutarı",
    idempotencyKey: "Tekillik anahtarı",
    paymentId: "Ödeme",
    accountId: "Cari",
    reason: "Gerekçe",
    description: "Açıklama",
    ipAddress: "IP adresi",
    userAgent: "Tarayıcı bilgisi",
    operation: "İşlem",
    refundType: "İşlem kodu",
    providerReference: "Sağlayıcı referansı",
    paymentAmount: "Ödeme tutarı",
    bankaKarti: "Banka kartıyla ödeme",
    closedAt: "Kapalı olduğu yer",
    cardBrand: "Kart markası",
    cardBrands: "Kart markaları",
    count: "Adet",
    changes: "Değişiklikler",
    changeCount: "Değişiklik sayısı",
    groupName: "Grup adı",
    openOptions: "Açık taksit seçeneği",
    logo: "Logo",
    favicon: "Favicon",
    // Firma kur ve sözleşme alanları
    usdMarkupRate: "USD kur farkı",
    eurMarkupRate: "EUR kur farkı",
    gbpMarkupRate: "GBP kur farkı",
    finalUsdRate: "USD kuru (manuel)",
    finalEurRate: "EUR kuru (manuel)",
    finalGbpRate: "GBP kuru (manuel)",
    fetchRatesAutomatically: "Kuru otomatik çek",
    onlineCollectionContract: "Online tahsilat sözleşmesi",
    serviceContract: "Hizmet sözleşmesi",
    cancellationReturnPrivacy: "İptal/iade ve gizlilik metni",
    personalDataPolicy: "KVKK metni",
    companyId: "Firma",
    accountGroupId: "Taksit grubu",
    // Abonelik ve ek modül
    type: "Tür",
    upgradeType: "Yükseltme türü",
    planName: "Paket",
    previousPlan: "Önceki paket",
    nextPlan: "Sonraki paket",
    startDate: "Başlangıç",
    endDate: "Bitiş",
    activationDate: "Devreye giriş tarihi",
    plannedStartDate: "Planlanan başlangıç",
    trialEndDate: "Deneme bitişi",
    result: "Sonuç",
    period: "Dönem",
    couponCode: "Kupon kodu",
    creditBasisAmount: "Mahsup tutarı",
    paidAmount: "Ödenen tutar",
    refundAmount: "İade tutarı",
    failedReasonCode: "Hata kodu",
    maxAccounts: "Cari kullanıcı sınırı",
    maxUsers: "Kullanıcı sınırı",
    allowCustomDomain: "Özel alan adı",
    feature: "Özellik kodu",
    featureLabel: "Özellik",
    endsAt: "Bitiş",
    basePrice: "Liste fiyatı",
    discountedPrice: "İndirimli fiyat",
    listPrice: "Liste fiyatı",
    overridePrice: "Özel fiyat",
    duration: "Süre",
    visibleToCustomers: "Müşterilere görünür",
    quoteOnly: "Teklifle satılır",
    subscriptionCount: "Abonelik sayısı",
    priceOverrideCount: "Özel fiyat sayısı",
    offerOtherPlans: "Diğer paketleri de göster",
    visiblePlans: "Görünen paketler",
    discountType: "İndirim türü",
    discountValue: "İndirim",
    maxUsageCount: "Kullanım sınırı",
    perCompanyLimit: "Firma başına sınır",
    validFrom: "Geçerlilik başlangıcı",
    validUntil: "Geçerlilik bitişi",
    applicablePlanCount: "Geçerli paket sayısı",
    targetCompanyCount: "Hedef firma sayısı",
    stackableWithDiscount: "Diğer indirimle birleşir",
    moduleName: "Ek modül",
    source: "Kaynak",
    note: "Not",
    lifecycle: "Süre türü",
    vatRate: "KDV oranı",
    validityDays: "Geçerlilik (gün)",
    features: "Özellikler",
    subscriptionGroup: "Paket ödemesinde kullanılır",
    // Kullanıcı, firma, kayıt başvurusu
    targetEmail: "İşlem yapılan kullanıcı",
    companyCode: "Firma kodu",
    companyUsers: "Silinen firma kullanıcısı",
    accountUsers: "Silinen cari kullanıcısı",
    accounts: "Silinen cari",
    virtualPosSettings: "Silinen POS ayarı",
    applicantEmail: "Başvuran e-posta",
    applicantCompany: "Başvuran firma",
    // Cari ve mutabakat
    accountCode: "Cari kodu",
    accountName: "Cari adı",
    accountGroup: "Taksit grubu",
    accountType: "Cari türü",
    title: "Ünvan",
    deletedUserCount: "Silinen cari kullanıcısı",
    fileName: "Dosya adı",
    total: "Toplam",
    created: "Oluşturulan",
    updated: "Güncellenen",
    recipientEmail: "Alıcı e-posta",
    searchTerm: "Arama",
    // Dışa aktarma, rapor, ödeme
    exportType: "Dışa aktarılan",
    rowCount: "Satır sayısı",
    merchantOids: "Sipariş numaraları",
    accounted: "Muhasebeleştirme",
    recipient: "Alıcı",
    delivered: "Gönderildi",
    scheduleType: "Sıklık",
    scheduleDay: "Gönderim günü",
    minAmount: "En düşük tutar",
    statuses: "Durumlar",
    accountTypes: "Cari türleri",
    reportId: "Rapor",
    // Destek talebi
    ticketNo: "Talep no",
    subject: "Konu",
    priority: "Öncelik",
    assignee: "Atanan kişi",
    // Muhasebe entegrasyonları
    gizliAlanDegisti: "Gizli bilgi değişti",
    diaCariKodu: "DİA cari kodu",
    parasutFirmaNo: "Paraşüt firma no",
    sube: "Şube",
    posBankaHesabi: "POS banka hesabı",
};

const label = (key) => FIELD_LABELS[key] || key;

const renderValue = (value) => {
    if (value === null || value === undefined) return <span className="text-muted">—</span>;
    if (typeof value === "boolean") return value ? "Evet" : "Hayır";
    if (Array.isArray(value)) return value.join(", ");
    return String(value);
};

const AuditDetailDrawer = ({ logId, isOpen, onClose, onShowChain }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!isOpen || !logId) return;
        setLoading(true);
        setError("");
        fetchAuditLogDetail(logId)
            .then((res) => {
                if (res.statusCode === 200) setDetail(res.data);
                else setError(res.message || "Kayıt getirilemedi");
            })
            .catch(() => setError("Kayıt getirilemedi"))
            .finally(() => setLoading(false));
    }, [isOpen, logId]);

    const parsed = (json) => {
        if (!json) return null;
        try {
            return JSON.parse(json);
        } catch {
            // Ham metin çözümlenemiyorsa gizlemek yerine olduğu gibi göster.
            return json;
        }
    };

    const changes = detail ? parsed(detail.changes) : null;
    const metadata = detail ? parsed(detail.metadata) : null;

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg" centered scrollable>
            <ModalHeader toggle={onClose}>Denetim Kaydı</ModalHeader>
            <ModalBody>
                {loading && <div className="text-center py-4"><Spinner /></div>}
                {error && <div className="alert alert-danger">{error}</div>}

                {detail && !loading && (
                    <>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                            <h5 className="mb-0">{detail.actionLabel}</h5>
                            <Badge color="light" className="text-dark">{detail.categoryLabel}</Badge>
                            <Badge color={detail.outcome === "SUCCESS" ? "success"
                                : detail.outcome === "FAILURE" ? "danger" : "warning"}>
                                {detail.outcomeLabel}
                            </Badge>
                            {detail.impersonated && (
                                <Badge color="dark" title="Bir kullanıcı adına yapılmış işlem">
                                    Adına işlem
                                </Badge>
                            )}
                        </div>

                        <dl className="row mb-3">
                            <dt className="col-sm-4">Zaman</dt>
                            <dd className="col-sm-8">{formatDateTime(detail.occurredAt)}</dd>

                            <dt className="col-sm-4">Yapan</dt>
                            <dd className="col-sm-8">
                                {detail.actorName || <span className="text-muted">—</span>}
                                {detail.actorEmail && <div className="text-muted small">{detail.actorEmail}</div>}
                                {detail.actorRole && <div className="text-muted small">{detail.actorRole}</div>}
                            </dd>

                            {detail.onBehalfOfUserId && (
                                <>
                                    <dt className="col-sm-4">Adına işlem yapılan</dt>
                                    <dd className="col-sm-8">
                                        {detail.onBehalfOfEmail || <IdValue value={detail.onBehalfOfUserId} />}
                                    </dd>
                                </>
                            )}

                            <dt className="col-sm-4">Firma</dt>
                            <dd className="col-sm-8">
                                {detail.companyName || <span className="text-muted">Platform seviyesi</span>}
                            </dd>

                            <dt className="col-sm-4">Etkilenen kayıt</dt>
                            <dd className="col-sm-8">
                                {entityTypeLabel(detail.entityType) || "—"}
                                {detail.entityId && <div className="text-muted small"><IdValue value={detail.entityId} /></div>}
                            </dd>

                            {detail.referenceKey && (
                                <>
                                    <dt className="col-sm-4">Referans</dt>
                                    <dd className="col-sm-8"><code>{detail.referenceKey}</code></dd>
                                </>
                            )}

                            {(detail.failureCode || detail.failureMessage) && (
                                <>
                                    <dt className="col-sm-4">Hata</dt>
                                    <dd className="col-sm-8">
                                        {detail.failureCode && <code>{detail.failureCode}</code>}
                                        {detail.failureMessage && <div>{detail.failureMessage}</div>}
                                    </dd>
                                </>
                            )}

                            {detail.ipAddress && (
                                <>
                                    <dt className="col-sm-4">IP</dt>
                                    <dd className="col-sm-8">
                                        <code>{detail.ipAddress}</code>
                                        {detail.userAgent && (
                                            <div className="text-muted small text-truncate" title={detail.userAgent}>
                                                {detail.userAgent}
                                            </div>
                                        )}
                                    </dd>
                                </>
                            )}
                        </dl>

                        {/* Değişen alanlar — sır niteliğindeki alanların değeri backend'de
                            hiç yazılmaz, yalnız "değiştirildi" bilgisi gelir. */}
                        {changes && typeof changes === "object" && (
                            <>
                                <h6 className="mt-4">Değişen alanlar</h6>
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr><th>Alan</th><th>Önce</th><th>Sonra</th></tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(changes).map(([field, change]) => (
                                            <tr key={field}>
                                                <td>{label(field)}</td>
                                                {change && change.changed ? (
                                                    <td colSpan={2}>
                                                        <Badge color="light" className="text-dark">
                                                            Değiştirildi — değeri kaydedilmez
                                                        </Badge>
                                                    </td>
                                                ) : (
                                                    <>
                                                        <td className="text-muted">{renderValue(change?.before)}</td>
                                                        <td>{renderValue(change?.after)}</td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {metadata && typeof metadata === "object" && (
                            <>
                                <h6 className="mt-4">Olay bilgileri</h6>
                                <table className="table table-sm align-middle">
                                    <tbody>
                                        {Object.entries(metadata).map(([key, value]) => (
                                            <tr key={key}>
                                                <td style={{ width: "40%" }}>{label(key)}</td>
                                                <td>{renderValue(value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {detail.correlationId && (
                            <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
                                <div className="text-muted small d-inline-flex align-items-center gap-1">
                                    İşlem zinciri: <IdValue value={detail.correlationId} />
                                </div>
                                <Button
                                    color="link"
                                    className="p-0"
                                    onClick={() => onShowChain(detail.correlationId)}
                                    title="Aynı kullanıcı eyleminden doğan tüm olayları listele"
                                >
                                    Zinciri göster
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

export default AuditDetailDrawer;
